import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as contactSelectors from "@rentiohq/shared-frontend/dist/redux/contact/contact.selectors";
import * as registrationActions from "@rentiohq/shared-frontend/dist/redux/registration/registration.actions";
import * as registrationSelectors from "@rentiohq/shared-frontend/dist/redux/registration/registration.selectors";
import { IContact } from "@rentiohq/shared-frontend/dist/types/contact.types";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import {
  ERegistrationDocumentType,
  IRegistration,
} from "@rentiohq/shared-frontend/dist/types/registration.types";
import {
  Error,
  Loading,
  MultiStepForm,
} from "@rentiohq/web-shared/dist/components";
import registrationAddDocumentForm from "forms/registrationAddDocument";
import {
  isPerformingSubmitActionSelector as isPerformingSubmitActionSelectorAddDocument,
  performingSubmitActionErrorSelector as performingSubmitActionErrorSelectorAddDocument,
  performingSubmitActionResultSelector as performingSubmitActionResultSelectorAddDocument,
  submitActionCreator as submitActionCreatorAddDocument,
} from "forms/registrationAddDocument/schema.registrationAddDocument.actions";
import { IExtraData as IExtraDataAddDocument } from "forms/registrationAddDocument/schema.registrationAddDocument.types";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IRootStore } from "redux/reducers";
import { NumberParam, createEnumParam } from "serialize-query-params";
import { ts } from "../../services";
import { isAddendumRegistrationPending } from "./RegistrationForm.utils";

export interface IProps {
  property: IProperty;
  contract: IContract;
  legalContact?: IContact;
}

export const AddendumRegistrationForm = (props: IProps) => {
  const { property, contract, legalContact } = props;

  const navigate = useNavigate();

  const [queryParams] = useQueryParams({
    id: NumberParam,
    documentType: createEnumParam(Object.values(ERegistrationDocumentType)),
    existingDocument: NumberParam,
  });

  const {
    id: registrationId,
    documentType = ERegistrationDocumentType.Addendum,
    existingDocument: existingDocumentId,
  } = queryParams;

  // Redux
  const dispatch = useDispatch();

  const { isBroker } = authHooks.useSelf();

  const [isLoading, setIsLoading] = React.useState(false);

  const contactMeMaster = useSelector((state: IRootStore) =>
    contactSelectors.getContactMeMaster(state),
  );

  const fetchError = useSelector((state: IRootStore) =>
    registrationSelectors.fetchError(state, registrationId),
  );

  const registration = useSelector((state: IRootStore) =>
    registrationSelectors.registration(state, registrationId),
  );
  const isFetchingRegistration = useSelector((state: IRootStore) =>
    registrationSelectors.isFetching(state, registrationId),
  );

  React.useEffect(() => {
    if (!registrationId) {
      return;
    }

    dispatch(
      registrationActions.getRegistration.actions.start({
        id: registrationId,
      }),
    );
  }, []);

  const onSuccess = (registration: IRegistration) => {
    const isPending = isAddendumRegistrationPending(registration);

    if (isPending) {
      fetchRegistrationWithTimeout(registration.id);
    } else {
      navigate(`/properties/${property.id}?registrationId=${registration.id}`);
    }
  };

  function fetchRegistration(id: number) {
    dispatch(
      registrationActions.getRegistration.actions.start({
        id,
        onSuccess: registration =>
          registration &&
          navigate(
            `/properties/${property.id}?registrationId=${registration.id}`,
          ),
      }),
    );
  }

  function fetchRegistrationWithTimeout(id: number) {
    setIsLoading(true);

    setTimeout(() => {
      fetchRegistration(id);
    }, 10000);
  }

  // Render
  if (fetchError) {
    return <Error errors={[fetchError]} />;
  }

  if (registration) {
    const extraData: IExtraDataAddDocument = {
      documentType,
      registrationId: registration.id,
      registration,
      existingDocumentId,
      contract,
      property,
      contactMeMaster,
      legalContact,
      isBroker,
    };

    if (isLoading || isFetchingRegistration) {
      return <Loading />;
    }

    return (
      <MultiStepForm
        formId={`registration-document-${property.id}-${contract.id}`}
        schemas={registrationAddDocumentForm(extraData)}
        withSummary={true}
        submitLabel={ts.system("save_send")}
        isPerformingSubmitActionSelector={isPerformingSubmitActionSelectorAddDocument(
          extraData,
        )}
        performingSubmitActionResultSelector={performingSubmitActionResultSelectorAddDocument(
          extraData,
        )}
        performingSubmitActionErrorSelector={performingSubmitActionErrorSelectorAddDocument(
          extraData,
        )}
        submitActionCreator={(formData: any) =>
          submitActionCreatorAddDocument(extraData)(formData)
        }
        onSuccess={onSuccess}
      />
    );
  }
};
